// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — AdminListSection (AD-11)
//
// Reusable component that shows an org's admin roster with email, role, and
// actions (Remove / Edit Role). Used in PlatformAdminPage (Entry Point A)
// and SettingsPage (Entry Point B).

import { useState, useCallback } from 'react';
import { callFunction, AppError } from '../services/firebase';

const ADMIN_ROLES_DISPLAY: Record<string, string> = {
  admin:        'Admin',
  org_creator:  'Creator',
  org_approver: 'Approver',
  offer_only:   'Offer Only',
  member:       'Member',
};

const EDITABLE_ROLES = ['admin', 'org_approver', 'offer_only', 'member'] as const;
const INVITE_ROLES   = ['admin', 'org_creator', 'org_approver'] as const;

interface AdminEntry {
  uid:         string;
  displayName: string | null;
  email:       string | null;
  role:        string;
}

interface Props {
  orgId:               string;
  orgName:             string;
  viewerIsPlatformAdmin: boolean;
  viewerUid:           string;
  /** When true, renders an "Invite Admin" form at the bottom (platform admin only). */
  showInvite?:         boolean;
}

export function AdminListSection({
  orgId,
  orgName,
  viewerIsPlatformAdmin,
  viewerUid,
  showInvite = false,
}: Props) {
  const [admins,          setAdmins]          = useState<AdminEntry[]>([]);
  const [loading,         setLoading]         = useState(false);
  const [loaded,          setLoaded]          = useState(false);
  const [error,           setError]           = useState<string | null>(null);

  // Remove state
  const [confirmRemoveUid, setConfirmRemoveUid] = useState<string | null>(null);
  const [removing,         setRemoving]         = useState(false);
  const [removeError,      setRemoveError]      = useState<string | null>(null);

  // Edit-role state
  const [editingUid,  setEditingUid]  = useState<string | null>(null);
  const [editRole,    setEditRole]    = useState('admin');
  const [savingRole,  setSavingRole]  = useState(false);
  const [roleError,   setRoleError]   = useState<string | null>(null);

  // Invite form state
  const [inviteEmail,   setInviteEmail]   = useState('');
  const [inviteRole,    setInviteRole]    = useState<typeof INVITE_ROLES[number]>('admin');
  const [inviting,      setInviting]      = useState(false);
  const [inviteError,   setInviteError]   = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const loadAdmins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await callFunction<{ admins: AdminEntry[] }>(
        'getOrgAdminsWithContact', { orgId }
      );
      setAdmins(result.admins ?? []);
      setLoaded(true);
    } catch (err) {
      setError((err as AppError).message ?? 'Failed to load admins.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const handleRemove = async (targetUid: string) => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await callFunction('removeOrgMember', { orgId, removeUid: targetUid });
      setAdmins(prev => prev.filter(a => a.uid !== targetUid));
      setConfirmRemoveUid(null);
    } catch (err) {
      setRemoveError((err as AppError).message ?? 'Failed to remove admin.');
    } finally {
      setRemoving(false);
    }
  };

  const handleSaveRole = async (targetUid: string) => {
    setSavingRole(true);
    setRoleError(null);
    try {
      await callFunction('updateOrgAdminRole', { orgId, targetUid, role: editRole });
      setAdmins(prev =>
        prev.map(a => a.uid === targetUid ? { ...a, role: editRole } : a)
      );
      setEditingUid(null);
    } catch (err) {
      setRoleError((err as AppError).message ?? 'Failed to update role.');
    } finally {
      setSavingRole(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(null);
    try {
      await callFunction('createOrgAdminInvite', {
        orgId,
        inviteeEmail: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
      });
      setInviteSuccess(`Invite sent to ${inviteEmail.trim()}.`);
      setInviteEmail('');
      setInviteRole('admin');
    } catch (err) {
      setInviteError((err as AppError).message ?? 'Failed to send invite.');
    } finally {
      setInviting(false);
    }
  };

  if (!loaded && !loading) {
    return (
      <button
        className="btn btn-outline btn-sm"
        onClick={loadAdmins}
        style={{ fontSize: 11 }}
      >
        Show admins
      </button>
    );
  }

  if (loading) {
    return <span className="spinner" style={{ width: 14, height: 14 }} />;
  }

  if (error) {
    return (
      <span style={{ fontSize: 11, color: 'var(--color-error)' }}>
        {error}{' '}
        <button className="btn btn-outline btn-sm" onClick={loadAdmins}>Retry</button>
      </span>
    );
  }

  return (
    <div style={{ fontSize: 13 }}>
      {admins.length === 0 ? (
        <div style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>No admins found.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {admins.map(admin => (
              <tr key={admin.uid}>
                {/* Name + email */}
                <td style={{ padding: '6px 0', verticalAlign: 'top', minWidth: 140 }}>
                  <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>
                    {admin.displayName ?? '(no name)'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                    {admin.email ?? '—'}
                  </div>
                </td>

                {/* Role */}
                <td style={{ padding: '6px 8px', verticalAlign: 'middle' }}>
                  {editingUid === admin.uid ? (
                    <select
                      className="form-select"
                      style={{ fontSize: 11, padding: '2px 6px', minWidth: 100 }}
                      value={editRole}
                      onChange={e => setEditRole(e.target.value)}
                    >
                      {EDITABLE_ROLES.map(r => (
                        <option key={r} value={r}>{ADMIN_ROLES_DISPLAY[r] ?? r}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="badge badge-gray" style={{ fontSize: 10 }}>
                      {ADMIN_ROLES_DISPLAY[admin.role] ?? admin.role}
                    </span>
                  )}
                </td>

                {/* Actions */}
                <td style={{ padding: '6px 0', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                  {editingUid === admin.uid ? (
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      {roleError && (
                        <span style={{ fontSize: 10, color: 'var(--color-error)', marginRight: 4 }}>
                          {roleError}
                        </span>
                      )}
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ fontSize: 11 }}
                        disabled={savingRole}
                        onClick={() => handleSaveRole(admin.uid)}
                      >
                        {savingRole ? <span className="spinner" /> : 'Save'}
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => { setEditingUid(null); setRoleError(null); }}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : confirmRemoveUid === admin.uid ? (
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      {removeError && (
                        <span style={{ fontSize: 10, color: 'var(--color-error)', marginRight: 4 }}>
                          {removeError}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                        Remove {admin.displayName ?? 'admin'}?
                      </span>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ fontSize: 11 }}
                        disabled={removing}
                        onClick={() => handleRemove(admin.uid)}
                      >
                        {removing ? <span className="spinner" /> : 'Yes'}
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => { setConfirmRemoveUid(null); setRemoveError(null); }}
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      {viewerIsPlatformAdmin && admin.role !== 'org_creator' && (
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ fontSize: 11 }}
                          onClick={() => {
                            setEditingUid(admin.uid);
                            setEditRole(admin.role);
                            setRoleError(null);
                          }}
                        >
                          Edit Role
                        </button>
                      )}
                      {admin.uid !== viewerUid && admin.role !== 'org_creator' && (
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ fontSize: 11 }}
                          onClick={() => { setConfirmRemoveUid(admin.uid); setRemoveError(null); }}
                        >
                          Remove
                        </button>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Invite form — platform admin only */}
      {showInvite && viewerIsPlatformAdmin && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--color-border)' }}>
          {inviteError && (
            <div className="error-banner" role="alert" style={{ marginBottom: 6, fontSize: 12 }}>
              {inviteError}
            </div>
          )}
          {inviteSuccess && (
            <div className="success-banner" role="status" style={{ marginBottom: 6, fontSize: 12 }}>
              {inviteSuccess}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '2 1 160px' }}>
              <label className="form-label" style={{ fontSize: 11 }}>
                Invite admin to <strong>{orgName}</strong>
              </label>
              <input
                type="email"
                className="form-input"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="admin@example.com"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 120px' }}>
              <label className="form-label" style={{ fontSize: 11 }}>Role</label>
              <select
                className="form-select"
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as typeof INVITE_ROLES[number])}
              >
                {INVITE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button
              className="btn btn-primary btn-sm"
              disabled={inviting || !inviteEmail.trim()}
              onClick={handleInvite}
            >
              {inviting ? <span className="spinner" /> : 'Send Invite'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
